import { Calendar } from "lucide-react";
import type { Dispatch, SetStateAction } from "react";

import type { AUTIrrigationPlan } from "@/api/queries/account";
import Button from "@/components/ui/Button";
import CollapsibleCard from "@/components/ui/CollapsibleCard";
import MultiSelectDropdown from "@/components/ui/MultiSelectDropdown";
import PeriodSelector from "@/components/ui/PeriodSelector";

import type { ReportFilters as ReportFiltersType } from "../types";

interface Project {
  id: string;
  name: string;
  irrigation_plans?: AUTIrrigationPlan[];
}

interface ReportFiltersProps {
  filters: ReportFiltersType;
  setFilters: Dispatch<SetStateAction<ReportFiltersType>>;
  projects: Project[];
  availablePlans: AUTIrrigationPlan[];
  error: string | null;
  loading: boolean;
  collapsed: boolean;
  onCollapse: Dispatch<SetStateAction<boolean>>;
  onApply: () => void;
}

/**
 * Report filters component with project, plan, and date range selection
 */
export function ReportFilters({
  filters,
  setFilters,
  projects,
  availablePlans,
  error,
  loading,
  collapsed,
  onCollapse,
  onApply,
}: ReportFiltersProps) {
  return (
    <CollapsibleCard title="Filtros do Relatório" collapsed={collapsed} onCollapse={onCollapse}>
      <div className="space-y-4">
        {/* Project Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Projeto</label>
          <select
            value={filters.projectId}
            onChange={(e) => setFilters((prev) => ({ ...prev, projectId: e.target.value, planIds: [] }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="">Selecione um projeto</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>

        {/* Plans Multi-select */}
        {availablePlans.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Planos (deixe em branco para todos)</label>
            <MultiSelectDropdown
              items={availablePlans}
              selectedIds={filters.planIds}
              onSelectedIdsChange={(selectedIds) => setFilters((prev) => ({ ...prev, planIds: selectedIds }))}
              placeholder="Selecione planos..."
              emptyMessage="Nenhum plano disponível"
              renderItem={(plan, _isSelected) => (
                <div className="flex items-center justify-between">
                  <span className={!plan.is_enabled ? "text-gray-500 italic" : ""}>
                    {plan.name}
                    {!plan.is_enabled && " (Desativado)"}
                  </span>
                </div>
              )}
              renderSelectedItem={(plan, itemCount) => (
                <div className="flex flex-col">
                  <span className={`font-medium text-left ${!plan.is_enabled ? "text-gray-500 italic" : ""}`}>
                    {itemCount === 1
                      ? `${plan.name}${!plan.is_enabled ? " (Desativado)" : ""}`
                      : `${plan.name} +${itemCount - 1}`}
                  </span>
                  <span className="text-xs text-gray-500">
                    {itemCount === 1 ? "plano selecionado" : `${itemCount} planos selecionados`}
                  </span>
                </div>
              )}
            />
          </div>
        )}

        {/* Period Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Período</label>
          <PeriodSelector
            value={{ start: filters.startDate, end: filters.endDate }}
            onChange={(range) => setFilters((prev) => ({ ...prev, startDate: range.start, endDate: range.end }))}
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Apply Button */}
        <Button
          onClick={onApply}
          loading={loading}
          disabled={!filters.projectId || !filters.startDate || !filters.endDate}
          variant="primary"
          size="md"
          icon={<Calendar className="h-4 w-4" />}
          iconPosition="left"
        >
          Aplicar Filtros
        </Button>
      </div>
    </CollapsibleCard>
  );
}
